import React from 'react';
import { useDashboard } from '../../contexts/DashboardContext';

const TaskModals = () => {
  const {
    API,
    appointments,
    axios,
    deleteTask,
    editingTask,
    editingTaskRule,
    handleTaskSubmit,
    isSearching,
    newTaskRule,
    products,
    setAutomatedTaskRules,
    setEditingTask,
    setEditingTaskRule,
    setNewTaskRule,
    setShowAddTaskRule,
    setShowTaskForm,
    setTaskForm,
    showAddTaskRule,
    showPropertyDropdown,
    showTaskForm,
    taskForm,
    teamMembers,
  } = useDashboard();

  return (
    <>
        {/* Automated Task Rule Modal */}
        {showAddTaskRule && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[150]">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 bg-gray-700 flex justify-between items-center sticky top-0">
                <h3 className="text-lg font-semibold text-white">
                  {editingTaskRule ? 'Edit Automation Rule' : 'Create Automation Rule'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddTaskRule(false);
                    setEditingTaskRule(null);
                    setNewTaskRule({
                      name: '',
                      product_ids: [],
                      team_member_filter: [],
                      task_title: '',
                      task_description: '',
                      assigned_to: '',
                      due_days_after_shoot: 0,
                      exclude_weekends: false,
                      priority: 'medium'
                    });
                  }}
                  className="text-white hover:text-gray-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (editingTaskRule) {
                    // Update existing rule
                    await axios.put(`${API}/automated-task-rules/${editingTaskRule.id}`, newTaskRule);
                    // Refresh rules list
                    const response = await axios.get(`${API}/automated-task-rules`);
                    setAutomatedTaskRules(response.data.rules);
                    alert('Rule updated successfully');
                  } else {
                    // Create new rule
                    await axios.post(`${API}/automated-task-rules`, newTaskRule);
                    // Refresh rules list
                    const response = await axios.get(`${API}/automated-task-rules`);
                    setAutomatedTaskRules(response.data.rules);
                    alert('Rule created successfully');
                  }
                  setShowAddTaskRule(false);
                  setEditingTaskRule(null);
                  setNewTaskRule({
                    name: '',
                    product_ids: [],
                    team_member_filter: [],
                    task_title: '',
                    task_description: '',
                    assigned_to: '',
                    due_days_after_shoot: 0,
                    exclude_weekends: false,
                    priority: 'medium'
                  });
                } catch (error) {
                  console.error('Error saving rule:', error);
                  alert('Failed to save rule');
                }
              }} className="p-6 space-y-6">
                {/* Rule Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rule Name *</label>
                  <input
                    type="text"
                    required
                    value={newTaskRule.name}
                    onChange={(e) => setNewTaskRule({...newTaskRule, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Upload photos task for all shoots"
                  />
                </div>

                {/* Criteria Section with Colored Background */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-3">Trigger Criteria</h4>
                  
                  {/* Trigger - Products */}
                  <div>
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      When these products are in an appointment
                      <span className="text-blue-400 text-xs ml-2">(leave empty for all products)</span>
                    </label>
                    <div className="bg-white border border-blue-300 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                      {products.length > 0 ? (
                        products.map(product => (
                          <label key={product.id} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={newTaskRule.product_ids.includes(product.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewTaskRule({
                                    ...newTaskRule,
                                    product_ids: [...newTaskRule.product_ids, product.id]
                                  });
                                } else {
                                  setNewTaskRule({
                                    ...newTaskRule,
                                    product_ids: newTaskRule.product_ids.filter(id => id !== product.id)
                                  });
                                }
                              }}
                              className="mr-2"
                            />
                            <span className="text-sm">{product.name}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No products available. Add products first.</p>
                      )}
                    </div>
                  </div>

                  {/* Team Member Filter */}
                  <div>
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      For appointments assigned to team member
                      <span className="text-blue-400 text-xs ml-2">(leave empty for all team members)</span>
                    </label>
                    <div className="bg-white border border-blue-300 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                      {teamMembers.length > 0 ? (
                        teamMembers.map(member => (
                          <label key={member.id} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={newTaskRule.team_member_filter.includes(member.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewTaskRule({
                                    ...newTaskRule,
                                    team_member_filter: [...newTaskRule.team_member_filter, member.id]
                                  });
                                } else {
                                  setNewTaskRule({
                                    ...newTaskRule,
                                    team_member_filter: newTaskRule.team_member_filter.filter(id => id !== member.id)
                                  });
                                }
                              }}
                              className="mr-2"
                            />
                            <span className="text-sm">{member.name}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No team members available. Add team members first.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Task Details */}
                <div className="border-t pt-6">
                  <h4 className="text-md font-semibold text-gray-700 mb-4">Create this task:</h4>
                  
                  <div className="space-y-4">
                    {/* Task Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Task Title *</label>
                      <input
                        type="text"
                        required
                        value={newTaskRule.task_title}
                        onChange={(e) => setNewTaskRule({...newTaskRule, task_title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Upload property photos"
                      />
                    </div>

                    {/* Task Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Task Description</label>
                      <textarea
                        value={newTaskRule.task_description}
                        onChange={(e) => setNewTaskRule({...newTaskRule, task_description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Additional details about the task..."
                      />
                    </div>

                    {/* Assign To */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Auto-assign to Team Member</label>
                      <select
                        value={newTaskRule.assigned_to}
                        onChange={(e) => setNewTaskRule({...newTaskRule, assigned_to: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Unassigned</option>
                        {teamMembers.map(member => (
                          <option key={member.id} value={member.id}>{member.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Due Days */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Due date (days after shoot)</label>
                      <input
                        type="number"
                        min="0"
                        value={newTaskRule.due_days_after_shoot}
                        onChange={(e) => setNewTaskRule({...newTaskRule, due_days_after_shoot: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Task will be due {newTaskRule.due_days_after_shoot} {newTaskRule.exclude_weekends ? 'business day' : 'day'}{newTaskRule.due_days_after_shoot !== 1 ? 's' : ''} after the scheduled shoot date
                      </p>
                      <label className="mt-3 flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!!newTaskRule.exclude_weekends}
                          onChange={(e) => setNewTaskRule({...newTaskRule, exclude_weekends: e.target.checked})}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          data-testid="exclude-weekends-checkbox"
                        />
                        <span className="text-sm text-gray-700">Exclude weekends from Due Dates</span>
                      </label>
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                      <select
                        value={newTaskRule.priority}
                        onChange={(e) => setNewTaskRule({...newTaskRule, priority: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddTaskRule(false);
                      setEditingTaskRule(null);
                      setNewTaskRule({
                        name: '',
                        product_ids: [],
                        team_member_filter: [],
                        task_title: '',
                        task_description: '',
                        assigned_to: '',
                        due_days_after_shoot: 0,
                        exclude_weekends: false,
                        priority: 'medium'
                      });
                    }}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                  >
                    {editingTaskRule ? 'Update Rule' : 'Create Rule'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Task Form Modal */}
        {showTaskForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[150]">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] md:max-w-2xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-100">
              {/* Modern Header */}
              <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${editingTask ? 'bg-indigo-100' : 'bg-emerald-100'}`}>
                    <svg className={`w-4 h-4 ${editingTask ? 'text-indigo-600' : 'text-emerald-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {editingTask ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      )}
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      {editingTask ? 'Edit Task' : 'New Task'}
                    </h3>
                    <p className="text-xs text-gray-500">{editingTask ? 'Update task details below' : 'Add a task and assign it to a property or team member'}</p>
                  </div>
                  {editingTask?.is_automated && (
                    <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-semibold uppercase tracking-wide rounded-full flex items-center gap-1">
                      ⚡ Automated
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowTaskForm(false);
                    setEditingTask(null);
                    setTaskForm({ appointment_id: '', title: '', description: '', due_date: '', assigned_to: '', priority: 'medium', status: 'pending' });
                  }}
                  className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
                  aria-label="Close"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleTaskSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="px-6 py-5 space-y-6 overflow-y-auto flex-1">
                  {/* Title hero input */}
                  <div>
                    <input
                      type="text"
                      value={taskForm.title}
                      onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                      className="w-full text-2xl font-semibold text-gray-900 bg-transparent border-0 outline-none focus:ring-0 placeholder-gray-300 px-0"
                      placeholder="What needs to be done?"
                      autoFocus={!editingTask}
                      required
                    />
                    <div className="h-px bg-gray-100 mt-2" />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</label>
                    <textarea
                      value={taskForm.description}
                      onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 placeholder-gray-400 resize-none"
                      rows="3"
                      placeholder="Add notes, links, or context for this task…"
                    />
                  </div>

                  {/* Property / Appointment */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Property / Appointment</label>
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                      <input
                        type="text"
                        value={taskForm.propertySearch || ''}
                        onChange={(e) => {
                          setTaskForm({ ...taskForm, propertySearch: e.target.value, showPropertyDropdown: true, propertyHighlightIndex: 0 });
                        }}
                        onFocus={() => setTaskForm({ ...taskForm, showPropertyDropdown: true, propertyHighlightIndex: 0 })}
                        onBlur={() => setTimeout(() => setTaskForm(prev => ({ ...prev, showPropertyDropdown: false })), 200)}
                        onKeyDown={(e) => {
                          const isSearching = taskForm.propertySearch && taskForm.propertySearch !== 'General Task';
                          const filteredApts = appointments.filter(apt =>
                            !isSearching ||
                            apt.property_address?.toLowerCase().includes(taskForm.propertySearch.toLowerCase()) ||
                            apt.client_name?.toLowerCase().includes(taskForm.propertySearch.toLowerCase())
                          );
                          const displayList = isSearching ? filteredApts : filteredApts.slice(0, 5);
                          const totalOptions = displayList.length + 1;
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setTaskForm(prev => ({ ...prev, propertyHighlightIndex: Math.min((prev.propertyHighlightIndex || 0) + 1, totalOptions - 1) }));
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setTaskForm(prev => ({ ...prev, propertyHighlightIndex: Math.max((prev.propertyHighlightIndex || 0) - 1, 0) }));
                          } else if (e.key === 'Enter' && taskForm.showPropertyDropdown) {
                            e.preventDefault();
                            const idx = taskForm.propertyHighlightIndex || 0;
                            if (idx === 0) {
                              setTaskForm({ ...taskForm, appointment_id: 'general', propertySearch: 'General Task', showPropertyDropdown: false });
                            } else {
                              const apt = displayList[idx - 1];
                              if (apt) {
                                setTaskForm({ ...taskForm, appointment_id: apt.id, propertySearch: `${apt.property_address} - ${apt.client_name}`, showPropertyDropdown: false });
                              }
                            }
                          } else if (e.key === 'Escape') {
                            setTaskForm(prev => ({ ...prev, showPropertyDropdown: false }));
                          }
                        }}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                        placeholder="Search properties or pick General Task…"
                      />
                      {taskForm.showPropertyDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          <button
                            type="button"
                            onClick={() => setTaskForm({ ...taskForm, appointment_id: 'general', propertySearch: 'General Task', showPropertyDropdown: false })}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b ${(taskForm.propertyHighlightIndex || 0) === 0 ? 'bg-blue-50' : ''}`}
                          >
                            <span className="font-medium text-gray-900">📋 General Task</span>
                            <span className="block text-xs text-gray-500">Not assigned to any property</span>
                          </button>
                          {(() => {
                            const isSearchingNow = taskForm.propertySearch && taskForm.propertySearch !== 'General Task';
                            const filtered = appointments.filter(apt =>
                              !isSearchingNow ||
                              apt.property_address?.toLowerCase().includes(taskForm.propertySearch.toLowerCase()) ||
                              apt.client_name?.toLowerCase().includes(taskForm.propertySearch.toLowerCase())
                            );
                            const displayList = isSearchingNow ? filtered : filtered.slice(0, 5);
                            return displayList.map((apt, idx) => (
                              <button
                                type="button"
                                key={apt.id}
                                onClick={() => setTaskForm({ ...taskForm, appointment_id: apt.id, propertySearch: `${apt.property_address} - ${apt.client_name}`, showPropertyDropdown: false })}
                                className={`w-full text-left px-4 py-2 hover:bg-gray-50 text-sm ${(taskForm.propertyHighlightIndex || 0) === idx + 1 ? 'bg-blue-50' : ''}`}
                              >
                                <span className="font-medium text-gray-900">{apt.property_address}</span>
                                <span className="block text-xs text-gray-500">{apt.client_name}</span>
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Details Grid (Due, Priority, Status, Assignee) */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Details</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          <span className="text-xs font-medium text-gray-700">Due Date *</span>
                        </div>
                        <input
                          type="date"
                          value={taskForm.due_date}
                          onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                          className="w-full bg-white border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                          required
                        />
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" /></svg>
                          <span className="text-xs font-medium text-gray-700">Priority</span>
                        </div>
                        <div className="flex gap-1.5">
                          {['low', 'medium', 'high'].map(p => {
                            const active = taskForm.priority === p;
                            const colors = {
                              low: active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50',
                              medium: active ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200 hover:bg-amber-50',
                              high: active ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-200 hover:bg-red-50',
                            };
                            return (
                              <button
                                key={p}
                                type="button"
                                onClick={() => setTaskForm({ ...taskForm, priority: p })}
                                className={`flex-1 text-xs font-medium px-2 py-1.5 rounded-md border transition-colors capitalize ${colors[p]}`}
                              >
                                {p}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <span className="text-xs font-medium text-gray-700">Status</span>
                        </div>
                        <select
                          value={taskForm.status}
                          onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                          className="w-full bg-white border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          <span className="text-xs font-medium text-gray-700">Assignee</span>
                        </div>
                        <select
                          value={taskForm.assigned_to}
                          onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                          className="w-full bg-white border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                        >
                          <option value="">Unassigned</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name} — {member.role}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sticky Footer */}
                <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-white">
                  {editingTask ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
                          deleteTask(editingTask.id);
                          setShowTaskForm(false);
                          setEditingTask(null);
                          setTaskForm({ appointment_id: '', title: '', description: '', due_date: '', assigned_to: '', priority: 'medium', status: 'pending' });
                        }
                      }}
                      className="text-sm font-medium text-red-600 hover:text-red-800 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3" /></svg>
                      Delete
                    </button>
                  ) : <span />}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowTaskForm(false);
                        setEditingTask(null);
                        setTaskForm({ appointment_id: '', title: '', description: '', due_date: '', assigned_to: '', priority: 'medium', status: 'pending' });
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm flex items-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      {editingTask ? 'Update Task' : 'Create Task'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
    </>
  );
};

export default TaskModals;
